import { useState } from "react";

import { createClient } from "graphql-ws";

import {
    ufoEventGraphQLSubscriptionSetup,
    ufoEventGraphQLWebhookSetup,
    ProductKey
} from "@/components/signup/utils";

const {
    InMemoryCache,
    HttpLink,
    split,
    ApolloClient
} = require("@apollo/client");
import("@apollo/client/dev")
    .then(({ loadErrorMessages, loadDevMessages }) => {
        loadErrorMessages();
        loadDevMessages();
    })
    .catch((error) => {
        console.error("Error loading Apollo client devtools:", error);
    });

type UseGraphqlClientOptions = {
    readonly reactComponent: string;
    readonly orchestrationId?: string;
    readonly onErrorCb?: (error: unknown) => void;
    readonly logParams?: {
        readonly productKey?: ProductKey;
        readonly bundleProductKeys?: string;
    };
};

export const useGraphqlClient = (options: UseGraphqlClientOptions) => {
    const [apolloClient, setApolloClient] = useState(null);
    const [apolloClientError, setApolloClientError] = useState<unknown>(null);
    const {
        orchestrationId,
        reactComponent,
        logParams = {},
        onErrorCb
    } = options;
    const { productKey, bundleProductKeys } = logParams;

    const setPlaceholderApolloClient = () => {
        setApolloClient(
            new ApolloClient({
                uri: `${window.location.protocol}//example.com/graphql`,
                cache: new InMemoryCache()
            })
        );
    };

    const initializeApolloClient = () => {
        if (!apolloClient) {
            try {
                // Start UFO to measure performance and reliability of GraphQL webhook initialization
                ufoEventGraphQLWebhookSetup.start({
                    // @ts-ignore
                    productKey,
                    bundleProductKeys
                });

                if (!orchestrationId) {
                    setPlaceholderApolloClient();
                } else {
                    ufoEventGraphQLSubscriptionSetup.start({
                        // @ts-ignore
                        productKey,
                        bundleProductKeys
                    });
                    const {
                        GraphQLWsLink
                    } = require("@apollo/client/link/subscriptions");
                    const {
                        getMainDefinition
                    } = require("@apollo/client/utilities");
                    const graphqlHost =
                        process.env["NODE_ENV"] === "production"
                            ? "www.atlassian.com/gateway/api"
                            : "wac.stg.internal.atlassian.com/gateway/api";

                    const httpGraphqlLink = new HttpLink({
                        uri: `https://${graphqlHost}/graphql`
                    });

                    const wsGraphqlLink = new GraphQLWsLink(
                        createClient({
                            url: `wss://${graphqlHost}/graphql/subscriptions`,
                            connectionParams: {
                                protocols: ["graphql-transport-ws"]
                            }
                        })
                    );
                    const apolloClientLink = split(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ({ query }: any) => {
                            const definition = getMainDefinition(query);
                            return (
                                definition.kind === "OperationDefinition" &&
                                definition.operation === "subscription"
                            );
                        },
                        wsGraphqlLink,
                        httpGraphqlLink
                    );
                    const apolloClientCache = new InMemoryCache({
                        addTypename: false
                    });
                    setApolloClient(
                        new ApolloClient({
                            link: apolloClientLink,
                            cache: apolloClientCache
                        })
                    );
                    // SLO "good event" for graphql webhook setup
                    ufoEventGraphQLWebhookSetup.success({
                        customData: {
                            reactComponent,
                            message: "subscription client",
                            orchestrationId
                        }
                    });
                }
            } catch (error) {
                // SLO "bad event" for graphql webhook setup
                ufoEventGraphQLWebhookSetup.failure({
                    error,
                    customData: {
                        reactComponent,
                        errorOrigin: "initialization",
                        orchestrationId
                    }
                });
                setPlaceholderApolloClient();
                setApolloClientError(error);
                onErrorCb?.(error);
            }
        }
    };
    initializeApolloClient();

    return { apolloClient, apolloClientError };
};
